import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SettingsAdminService, WaitingListStatus, ContactsListStatusDef } from "../services/settingsAdminService";
import type { ApiError } from "@/shared/utils/fetchApi";

interface AddContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listType: string;
  statuses: ContactsListStatusDef[];
}

export function AddContactDialog({ open, onOpenChange, listType, statuses }: AddContactDialogProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split("-")[0];
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<WaitingListStatus>("available");
  const [emailError, setEmailError] = useState("");

  const mutation = useMutation({
    mutationFn: () => SettingsAdminService.addContact(listType, email, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-setting", listType] });
      setEmail("");
      setStatus("pending");
      setEmailError("");
      onOpenChange(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      setEmailError(t("settings.waitlists.addDialog.invalidEmail"));
      return;
    }
    setEmailError("");
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("settings.waitlists.addDialog.title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="email">{t("settings.waitlists.addDialog.email")}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("settings.waitlists.addDialog.emailPlaceholder")}
              autoFocus
            />
            {emailError && <p className="text-xs text-destructive">{emailError}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="status">{t("settings.waitlists.addDialog.status")}</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as WaitingListStatus)}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label[lang as "en" | "pt"] ?? s.label.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {mutation.isError && (
            <p className="text-xs text-destructive">
              {((mutation.error as ApiError)?.data as { error?: string })?.error ?? (mutation.error as Error)?.message ?? t("settings.waitlists.addDialog.addError")}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("settings.waitlists.addDialog.cancel")}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t("settings.waitlists.addDialog.adding") : t("settings.waitlists.addDialog.add")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
